import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, ChevronDown, ChevronRight } from "lucide-react";

const RATING_LABELS = ["", "Nunca", "A veces", "A menudo", "Casi siempre", "Siempre"];

/**
 * Detailed view of a single evaluation, grouped by template block (category).
 * Shows every indicator with its rating (1..5), intention/success flags,
 * plus global average, objectives and observations.
 *
 * Props:
 *  - evaluation: {id, player_id, team_id, date, author_name, created_at, ratings, objectives, notes}
 *  - playerName, teamName, playerPosition (optional)
 *  - onClose
 */
export default function EvaluationDetailModal({ evaluation, playerName, teamName, playerPosition, onClose }) {
  const [template, setTemplate] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch categories + team info to resolve template key
        const [teamsResp, catsResp] = await Promise.all([
          api.get("/teams"),
          api.get("/categories"),
        ]);
        const team = teamsResp.data.find(t => t.id === evaluation.team_id);
        if (!team) return;
        const cat = catsResp.data.find(c => c.id === team.category_id);
        const key = playerPosition === "POR" ? "PORTERO" : (cat?.template_key || "U12");
        const tpl = await api.get(`/templates/${key}?team_id=${evaluation.team_id}`);
        if (!cancelled) {
          setTemplate(tpl.data);
          const exp = {};
          (tpl.data?.blocks || []).forEach((b, i) => { exp[b.id] = i === 0; });
          setExpanded(exp);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [evaluation.team_id, playerPosition]);

  const ratingByIndicator = {};
  (evaluation.ratings || []).forEach(r => { ratingByIndicator[r.indicator_id] = r; });

  const nums = (evaluation.ratings || [])
    .filter(r => r.rating !== undefined && r.rating !== null)
    .map(r => Number(r.rating));
  const avg = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border w-full max-w-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="eval-detail-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Detalle de evaluación</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">{playerName}</div>
            <div className="text-xs font-mono text-muted-foreground">{teamName} · {evaluation.date}</div>
          </div>
          <button data-testid="close-eval-detail-btn" onClick={onClose} className="p-1 hover:text-primary"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-border p-4 text-center md:col-span-1">
            <div className="overline">Media global</div>
            <div className="metric-number text-4xl mt-2 text-primary">
              {isFinite(avg) && avg > 0 ? avg.toFixed(1) : "—"}
              <span className="text-lg text-muted-foreground">/5</span>
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mt-1">{nums.length} indicadores evaluados</div>
          </div>
          <div className="border border-border p-4 md:col-span-2">
            <div className="overline mb-2">Registrada por</div>
            <div className="font-heading font-bold text-base">{evaluation.author_name || "—"}</div>
            <div className="text-[11px] font-mono text-muted-foreground mt-1">
              {evaluation.created_at?.slice(0, 16).replace("T", " ")}
            </div>
          </div>
        </div>

        {/* Ratings by category */}
        {template ? (
          <div className="space-y-2">
            {(template.blocks || []).map((b) => {
              const rated = (b.indicators || []).filter(ind => ratingByIndicator[ind.id]);
              const blockNums = rated
                .map(ind => Number(ratingByIndicator[ind.id]?.rating))
                .filter(v => !isNaN(v));
              const blockAvg = blockNums.length ? (blockNums.reduce((a, c) => a + c, 0) / blockNums.length) : null;
              return (
                <div key={b.id} className="border border-border" data-testid={`eval-detail-block-${b.id}`}>
                  <button type="button" onClick={() => setExpanded(x => ({ ...x, [b.id]: !x[b.id] }))} className="w-full flex items-center gap-2 p-3 hover:bg-white/[0.02] text-left">
                    {expanded[b.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="font-heading font-bold uppercase tracking-tight text-sm">{b.name}</span>
                    <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                      {rated.length}/{(b.indicators || []).length} indicadores
                      {blockAvg !== null && <span className="ml-2 text-primary">· media {blockAvg.toFixed(1)}/5</span>}
                    </span>
                  </button>
                  {expanded[b.id] && (
                    <div className="border-t border-border p-3 space-y-2">
                      {(b.indicators || []).map((ind) => {
                        const r = ratingByIndicator[ind.id];
                        return (
                          <div key={ind.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center text-sm py-1 border-b border-border/40 last:border-b-0">
                            <div className="md:col-span-7">{ind.name}</div>
                            <div className="md:col-span-3 flex gap-1">
                              {[1, 2, 3, 4, 5].map((v) => (
                                <div key={v} title={RATING_LABELS[v]}
                                  className={`w-7 h-7 border text-[11px] font-mono flex items-center justify-center ${r?.rating === v ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground/60"}`}>
                                  {v}
                                </div>
                              ))}
                            </div>
                            <div className="md:col-span-2 text-[10px] font-mono text-muted-foreground text-right">
                              {r?.rating ? `${r.rating}/5 · ${RATING_LABELS[r.rating]}` : "Sin valorar"}
                              {r?.intention && <div className="text-primary">✓ Intención</div>}
                              {r?.success && <div className="text-emerald-400">✓ Éxito</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground text-center py-6">Cargando plantilla de indicadores…</div>
        )}

        {evaluation.objectives && (
          <div className="border-l-4 border-primary bg-primary/5 p-4">
            <div className="overline text-primary mb-2">Objetivos próximo periodo</div>
            <div className="text-sm whitespace-pre-wrap">{evaluation.objectives}</div>
          </div>
        )}
        {evaluation.notes && (
          <div className="border border-border p-4">
            <div className="overline mb-2">Observaciones</div>
            <div className="text-sm whitespace-pre-wrap">{evaluation.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}
